import type { TimelineRow } from '@tabel/shared/agent-events/timeline';
import type { ThreadDetail, ThreadView } from '@tabel/shared/schemas/agent-client';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { MessageSquareOff } from 'lucide-react';
import { type ReactNode, type RefObject, useCallback, useEffect, useRef, useState } from 'react';
import {
  cancelRetry,
  checkAgent,
  createThread,
  dropQueued,
  isGone,
  patchThread,
  resolveInteraction,
  retryRun,
  sendMessage,
  stopAgent,
} from '../../lib/agent-api';
import { ApiError } from '../../lib/errors';
import { useToast } from '../ErrorToast';
import { Button } from '../ui';
import { Composer, type ComposerMode, type Outgoing, newMessageKey } from './Composer';
import { Feed, type PendingRow } from './Feed';
import { MemoryRing } from './MemoryRing';
import { ModelPicker, useAgentModels } from './ModelPicker';
import { Greeting, StateStrip, type StripState, stripOf } from './StateStrip';
import { HINTS, LABEL, T, TITLE } from './texts';
import type { RequestRow } from './types';
import {
  type AgentChoice,
  threadKey,
  threadsKey,
  useAgentComputers,
  useThreadDetail,
  useThreadTimeline,
} from './useThread';

/*
 * «Разговор» (T074; contracts/ui.md §2, §3; FR-031–FR-040): лента, полоса состояния и поле сообщения одного разговора.
 * Один компонент для правой панели (`AssistantPanel`) и страницы «Помощник» (колонка до 760 px); раскладку задаёт
 * место показа через `children` — части приходят по отдельности: полоса — под шапкой, лента — в теле, поле — внизу.
 * - Новый разговор (`threadId = null`): приветствие с подсказками по странице; разговор создаётся первым сообщением
 *   на выбранном компьютере и агенте (`choice`).
 * - Сообщение принято, а агент ещё не начал работу — оно видно в ленте с «Агент начинает работу…», пока не придёт
 *   строка с сервера с тем же ключом.
 * - Запрос агента с одноразовым номером заменяет поле карточкой; ответ — `POST …/interactions/:iid/resolve`.
 * - Полоса — одна, самая важная (`stripOf`); строку ошибки модели, которую показывает полоса, лента не повторяет.
 */

export interface ConversationParts {
  thread: ThreadView | null;
  /** Полоса состояния под шапкой. */
  status: ReactNode;
  /** Лента. */
  feed: ReactNode;
  /** Поле сообщения; разговор в архиве — `null`. */
  dock: ReactNode;
}

export interface ConversationProps {
  /** `null` — новый разговор: создаётся при первом сообщении. */
  threadId: string | null;
  /** Откуда начат разговор (`page` нового разговора). */
  page: 'rules' | 'day' | 'assistant';
  /** Компьютер и агент нового разговора. */
  choice: AgentChoice | null;
  /** Текст для поля: `tick` меняется — поле заполняется (сообщение не уходит само). */
  draft?: { text: string; tick: number } | null;
  /** Подсказки по странице до первого сообщения (FR-038). */
  hints: readonly string[];
  /** Часовой пояс объекта. */
  tz: string;
  layout: 'panel' | 'page';
  onCreated: (thread: ThreadView) => void;
  onOpenProject: (planId: number) => void;
  /** «Начать новый разговор», «Новый разговор» в сообщении «Разговора нет». */
  onNewThread: () => void;
  /** «Открыть «Мои ИИ-клиенты»». */
  onOpenMyAgents: () => void;
  /** Поле сообщения — для фокуса при открытии панели. */
  inputRef?: RefObject<HTMLTextAreaElement | null>;
  children: (parts: ConversationParts) => ReactNode;
}

const NOT_SENDABLE: ReadonlySet<StripState['kind']> = new Set([
  'no_computer',
  'disabled',
  'login',
  'not_linked',
  'not_ready',
]);

/** Ждущий запрос агента, на который можно ответить: первый по порядку. */
function waitingRequest(rows: readonly TimelineRow[]): RequestRow | null {
  for (const r of rows) if (r.kind === 'request' && r.state === 'waiting' && r.nonce) return r;
  return null;
}

/** Агент работает: по состоянию разговора и по открытой работе в ленте. */
function isRunning(thread: ThreadView | null, rows: readonly TimelineRow[]): boolean {
  if (thread && thread.status !== 'idle' && thread.status !== 'error') return true;
  const last = [...rows].reverse().find((r) => r.kind === 'turn');
  return last?.kind === 'turn' && !last.collapsed;
}

export function Conversation(p: ConversationProps) {
  const qc = useQueryClient();
  const { showError, showInfo } = useToast();
  const computers = useAgentComputers();
  const detail = useThreadDetail(p.threadId);
  const tl = useThreadTimeline(p.threadId, detail.setStatus);
  const thread = detail.data?.thread ?? null;
  const queue = detail.data?.queue ?? [];
  const connectorId = thread?.connectorId ?? p.choice?.connectorId ?? null;
  const agent = thread?.agent ?? p.choice?.agent ?? null;
  const pc = computers.computer(connectorId);
  const health = computers.health(connectorId, agent);
  const missing = isGone(detail.error) || isGone(tl.error);

  // Тот же список и кэш, что у выбора модели: полоса «Список моделей не загрузился».
  const models = useAgentModels(connectorId ?? 0, agent ?? 'claude');

  /** Модель и уровень нового разговора: уходят в `POST /api/agent/threads`. */
  const [pick, setPick] = useState<{ model: string; effort: string | null } | null>(null);

  // Сообщения, которые сервер принял, а агент ещё не начал. У каждого — свой разговор: строка «Агент начинает
  // работу…» показывается только в нём (стенд 18.10: сообщение ушло в другой разговор, и строка висела).
  const [pending, setPending] = useState<(PendingRow & { threadId: string })[]>([]);
  /** Разговор, который создаёт первое сообщение: второе сообщение в это время идёт в тот же разговор. */
  const creating = useRef<Promise<string> | null>(null);
  const prevId = useRef(p.threadId);
  useEffect(() => {
    // Новый разговор создан первым сообщением — его сообщение остаётся; другой разговор — с чистого листа.
    if (prevId.current !== null && prevId.current !== p.threadId) setPending([]);
    if (p.threadId === null) creating.current = null;
    prevId.current = p.threadId;
  }, [p.threadId]);
  const visiblePending = pending.filter(
    (x) =>
      x.threadId === p.threadId &&
      !tl.rows.some((r) => r.key === `msg:${x.key}`) &&
      !tl.rows.some((r) => r.kind === 'system' && r.runId === null && r.seq > x.seq),
  );
  useEffect(() => {
    if (visiblePending.length !== pending.length) setPending(visiblePending);
  });

  const invalidate = useCallback(
    (id: string | null = p.threadId) => {
      if (!id) return;
      void qc.invalidateQueries({ queryKey: threadKey(id) });
    },
    [qc, p.threadId],
  );

  const strip = missing
    ? null
    : stripOf({
        thread,
        computer: pc,
        health,
        agent,
        rows: tl.rows,
        queue,
        modelsFallback: models.data?.fallback === true,
        lagging: tl.lagging,
        computersKnown: computers.available,
      });

  const running = isRunning(thread, tl.rows) || visiblePending.length > 0;
  const mode: ComposerMode =
    strip && NOT_SENDABLE.has(strip.kind) ? 'disabled' : running ? 'running' : 'idle';
  const request = waitingRequest(tl.rows);

  /** Разговор для сообщения: есть — он; нет — создаётся один раз, даже если сообщений два подряд. */
  const threadFor = (): Promise<string> => {
    if (p.threadId) return Promise.resolve(p.threadId);
    if (!creating.current) {
      if (!connectorId || !agent)
        return Promise.reject(new ApiError(0, 'AGENT_NOT_READY', T.noComputer, null));
      creating.current = createThread({
        connectorId,
        agent,
        page: p.page,
        ...(pick ? { model: pick.model, ...(pick.effort ? { effort: pick.effort } : {}) } : {}),
      }).then(
        (t) => {
          qc.setQueryData<ThreadDetail>(threadKey(t.id), { thread: t, queue: [] });
          void qc.invalidateQueries({ queryKey: threadsKey(false) });
          p.onCreated(t);
          return t.id;
        },
        (e: unknown) => {
          creating.current = null;
          throw e;
        },
      );
    }
    return creating.current;
  };

  const send = async (m: Outgoing) => {
    const id = await threadFor();
    const res = await sendMessage(id, m);
    if (res.state === 'sent' && m.mode !== 'queue')
      setPending((list) => [
        ...list,
        { key: m.key, text: m.text, note: m.mode === 'steer' ? 'sent_now' : null, seq: tl.seq, threadId: id },
      ]);
    invalidate(id);
  };

  /** Подсказка отправляется один раз: второе нажатие, пока первое идёт, ничего не делает. */
  const [hintSending, setHintSending] = useState(false);
  const hintBusy = useRef(false);
  const sendHint = (text: string) => {
    if (hintBusy.current) return;
    hintBusy.current = true;
    setHintSending(true);
    void send({ text, mode: 'turn', key: newMessageKey() })
      .catch((e) => showError(e, { screen: 'Assistant', action: 'send hint' }))
      .finally(() => {
        hintBusy.current = false;
        setHintSending(false);
      });
  };

  const stop = useMutation({
    mutationFn: () => stopAgent(p.threadId ?? ''),
    onError: (e) => {
      // Агент уже не работает: строки «Агент начинает работу…» этого разговора устарели — экран возвращается в покой.
      if (e instanceof ApiError && e.code === 'TURN_NOT_RUNNING') {
        setPending((list) => list.filter((x) => x.threadId !== p.threadId));
        return;
      }
      showError(e, { screen: 'Assistant', action: 'stop agent' });
    },
    onSettled: () => {
      invalidate();
      tl.reload();
    },
  });

  const remove = useMutation({
    mutationFn: (key: string) => dropQueued(p.threadId ?? '', key),
    onError: (e) => showError(e, { screen: 'Assistant', action: 'drop queued' }),
    onSettled: () => invalidate(),
  });

  const resolve = async (
    row: RequestRow,
    decision: Parameters<typeof resolveInteraction>[2]['decision'],
    answers?: string[],
  ) => {
    try {
      await resolveInteraction(p.threadId ?? '', row.interruptId, {
        nonce: row.nonce ?? '',
        decision,
        ...(answers ? { answers } : {}),
      });
    } finally {
      tl.reload();
      invalidate();
    }
  };

  const check = useMutation({
    mutationFn: () => checkAgent(connectorId ?? 0, agent ?? 'claude'),
    onError: (e) => showError(e, { screen: 'Assistant', action: 'check agent' }),
    onSettled: () => void computers.refetch(),
  });
  const actionFailed = (action: string) => (e: unknown) => showError(e, { screen: 'Assistant', action });
  const stripActions = {
    openMyAgents: p.onOpenMyAgents,
    check: () => check.mutate(),
    cancelRetry: (runId: string) =>
      void cancelRetry(p.threadId ?? '', runId)
        .catch(actionFailed('cancel retry'))
        .finally(() => invalidate()),
    retry: (runId: string) =>
      void retryRun(p.threadId ?? '', runId)
        .catch(actionFailed('retry question'))
        .finally(() => {
          invalidate();
          tl.reload();
        }),
    newThread: p.onNewThread,
    reloadModels: () => void models.refetch(),
    restore: () =>
      void patchThread(p.threadId ?? '', { archived: false })
        .then((t) => {
          qc.setQueryData<ThreadDetail>(threadKey(t.id), (d) => (d ? { ...d, thread: t } : d));
          void qc.invalidateQueries({ queryKey: ['agent-threads'] });
          showInfo(T.restoredToast);
        })
        .catch(actionFailed('restore thread')),
    reload: tl.reload,
  };

  // Есть разговор — выбор модели сам записан на сервере (`ModelPicker`, `PATCH`); нет — уходит в создание разговора.
  const changeModel = (model: string, effort: string | null) => {
    if (!p.threadId) setPick({ model, effort });
    else invalidate();
  };

  const fresh = p.threadId === null || (tl.loaded && tl.rows.length === 0 && visiblePending.length === 0);
  const empty = missing ? (
    <div className="m-auto flex max-w-sm flex-col items-center gap-2 py-10 text-center">
      <span className="inline-flex h-11 w-11 items-center justify-center rounded-card bg-accent-soft text-accent">
        <MessageSquareOff size={20} strokeWidth={1.75} aria-hidden />
      </span>
      <h3 className="text-[15px] font-semibold text-ink">{TITLE.notFound}</h3>
      <p className="text-[13px] text-ink-2">{T.notFound}</p>
      <Button className="mt-2" onClick={p.onNewThread}>
        {LABEL.newThread}
      </Button>
    </div>
  ) : fresh ? (
    <Greeting
      hints={thread?.page === 'day' ? HINTS.day : p.hints}
      disabled={mode === 'disabled' || hintSending}
      onPick={sendHint}
    />
  ) : null;

  const feed = (
    <Feed
      threadId={p.threadId}
      rows={tl.rows}
      pending={visiblePending}
      hiddenKey={strip?.kind === 'model_error' ? strip.rowKey : null}
      busy={tl.rows.some(
        (r) => (r.kind === 'assistant' && r.streaming) || (r.kind === 'turn' && r.outcome === 'running'),
      )}
      loading={p.threadId !== null && !tl.loaded && !missing && !tl.error}
      empty={empty}
      onOpenProject={p.onOpenProject}
      layout={p.layout}
    />
  );

  const left =
    connectorId !== null && agent !== null ? (
      <>
        <ModelPicker
          connectorId={connectorId}
          agent={agent}
          threadId={p.threadId}
          model={thread?.model ?? pick?.model ?? null}
          effort={thread?.effort ?? pick?.effort ?? null}
          onChange={changeModel}
          disabled={mode === 'disabled'}
        />
        <MemoryRing percent={thread?.memPercent ?? null} />
      </>
    ) : null;

  const dock =
    thread?.archived || missing ? null : (
      <Composer
        mode={mode}
        request={request}
        queue={queue}
        draft={p.draft ?? null}
        stopping={stop.isPending}
        left={left}
        onSend={send}
        onStop={() => stop.mutate()}
        onRemove={(key) => remove.mutate(key)}
        onResolve={resolve}
        layout={p.layout}
        inputRef={p.inputRef}
      />
    );

  const status = strip ? (
    <StateStrip state={strip} tz={p.tz} actions={stripActions} checking={check.isPending} />
  ) : null;

  return <>{p.children({ thread, status, feed, dock })}</>;
}
